import {useState, useEffect, useRef} from "react";
import ParamRow from "./ParamRow";

const FieldModel = {
    name: "",
    length: 0,
    type: "bytes",
    description: "",
    raw: "",
    tokenBytes: 0,
    isSentinel: false,
};

const PayloadDefModel = function (payload = {}) {
    return {
        name: payload.name,
        type: payload.type,
        length: payload.length,
        description: payload.description,
        pad: payload.pad,
        special: payload.special || payload.values,
        range: payload.range,
        values: payload.values,
    };
};
// value = word.value, command = item
const TableParameters = ({item, word, setWord}) => {
    const [params, setParams] = useState([]);
    const fieldsValidated = useRef([]);

    useEffect(() => {
        const {fields, remaining} = splitPayload(word.value, item.parameters);
        setParams(fields);
        fieldsValidated.current = fieldsValidation(fields);
        const fieldsValid = fieldsValidated.current.every((validation) => validation?.valid !== false);
        const word_ = {...word};
        word_.currentBytes = calculateCurrentBytes();
        word_.totalBytes = calculateTotalBytes();
        word_.sameBytes = word_.currentBytes === word_.totalBytes;
        word_.isValid = word_.sameBytes && fieldsValid;
        setWord(word_);
    }, [word.value, item.parameters]);

    const parseCommaTokens = (raw) => raw.split(",").map((campo) => campo.trim());

    const mapPayloadDefs = (payloadArray = []) => payloadArray.map(PayloadDefModel);

    const buildField = (paramDef, tokenRaw = "", mode = "comma") => {
        const specials = paramDef.special || [];
        const field = {...FieldModel};
        field.name = paramDef.name;
        field.length = paramDef.length || 0;
        field.type = paramDef.type;
        field.description = paramDef.description;
        field.raw = tokenRaw;
        field.isSentinel = specials.includes(tokenRaw);
        if (mode === "comma" && paramDef.type === "bytes" && tokenRaw) {
            field.tokenBytes = Math.ceil(tokenRaw.length / 2);
        }
        return field;
    };

    const buildFieldsFromComma = (tokens = [], defsList = []) => {
        const fields = defsList.map((paramDef, i) => buildField(paramDef, tokens[i] || "", "comma"));
        const extraTokens = tokens.slice(defsList.length);
        const remaining = extraTokens.length > 0 ? extraTokens.join(",") : "";
        return {fields, remaining};
    };

    const splitPayload = (value = "", payloadDef = {}) => {
        const defs = mapPayloadDefs(payloadDef.payload || []);
        const isComma = (value || "").includes(",");
        if (isComma) {
            return buildFieldsFromComma(parseCommaTokens(value || ""), defs);
        }

        const hex = value || "";
        const fields = defs.map((paramDef, idx) => {
            // Calcular la posición inicial en caracteres hex (cada byte = 2 caracteres)
            let startPos = 0;
            for (let i = 0; i < idx; i++) {
                startPos += defs[i].length * 2;
            }
            // Extraer el valor hex para este campo
            const endPos = startPos + paramDef.length * 2;
            const hexValue = hex.substring(startPos, endPos);

            return buildField(paramDef, hexValue, "hex");
        });
        const remaining = hex || "";
        return {fields, remaining};
    };

    const isSpecialAllowed = (rawInput, defParam) => {
        const specials = defParam?.special || defParam?.values || [];
        if (specials.length === 0) {
            return false;
        }
        return specials.indexOf(rawInput) !== -1;
    };

    const validateField = (field, defParam) => {
        const rawInput = field?.raw;
        if (!rawInput) {
            return {valid: true};
        }

        if (isSpecialAllowed(rawInput, defParam)) {
            return {valid: true, reason: "Valor especial permitido"};
        }

        const type = defParam?.type || "string";
        const isBytes = type === "bytes";
        const processed = isBytes ? rawInput : rawInput;

        // Validar longitud (solo bytes)
        if (isBytes && defParam?.length > 0) {
            const expectedChars = defParam.length * 2;
            if (processed.length !== expectedChars) {
                return {
                    valid: false,
                    reason: `Longitud esperada ${defParam.length} bytes (${expectedChars} hex)`,
                };
            }
        }

        // Validar rango
        if (defParam?.range) {
            const num = parseInt(processed, isBytes ? 16 : 10);
            const {min, max} = defParam.range;
            if (num < min || num > max) {
                return {
                    valid: false,
                    reason: `Fuera de rango [${min ?? "-"}, ${max ?? "-"}]`,
                };
            }
        }

        // Validar valores permitidos
        if (defParam?.values?.length > 0) {
            if (defParam.values.indexOf(processed) === -1) {
                return {
                    valid: false,
                    reason: "Valor no permitido",
                };
            }
        }

        return {valid: true};
    };

    const calculateTotalBytes = () => {
        const payloadDefs = item.parameters.payload;
        if (!payloadDefs?.length) {
            return 0;
        }
        if (item.type === "string") {
            return payloadDefs.length;
        }

        return payloadDefs.reduce((sum, param) => {
            if (param?.type === "bytes") {
                return sum + (param.length || 0);
            }
            return sum;
        }, 0);
    };

    const calculateCurrentBytes = () => {
        if (!word.value) {
            return 0;
        }

        if (word.value.includes(",")) {
            if (word.value.at(-1) === ",") return word.value.split(/,/).length - 1;
            return word.value.split(/,/).length;
        }
        return word.value ? Math.ceil(word.value.length / 2) : 0;
    };

    const fieldsValidation = (fields) => {
        if (!item.parameters.payload.length) {
            return [];
        }
        return fields.map((p, idx) => validateField(p, item.parameters.payload[idx]));
    };

    return (
        <div>
            {params && params.length > 0 ? (
                <div className="table-responsive">
                    <table className="table table-sm table-striped">
                        <thead>
                            <tr>
                                <th className="text-center align-middle">Nombre</th>
                                <th className="text-center align-middle">Valor</th>
                                <th className="text-center align-middle">Decimal</th>
                                <th className="w-auto text-center align-middle">Bytes</th>
                                <th className="px-1">Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {params.map((field, idx) => (
                                <ParamRow key={idx} field={field} index={idx} item={item} fieldsValidated={fieldsValidated} />
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <small className="text-muted">No hay parámetros definidos para este comando.</small>
            )}
        </div>
    );
};

export default TableParameters;
